import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, api } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Loader2 } from 'lucide-react';
import ForgotPasswordDialog from '@/components/ForgotPasswordDialog';
import PhoneAuthPanel from '@/components/PhoneAuthPanel';
import CouplesSlideshow from '@/components/CouplesSlideshow';
import { launchGoogleSignIn, getGoogleAuthConfig } from '@/lib/googleAuth';

export default function AuthPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [searchParams] = useSearchParams();
  const initialRef = (searchParams.get('ref') || '').trim().toUpperCase();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', referral_code: initialRef });
  const [forgotOpen, setForgotOpen] = useState(false);
  // June 2026: sign-up is restricted to Google/Phone/Apple only.
  // Existing email/password users can still log in via the collapsible legacy
  // form; they're then forced to /link-account before proceeding.
  const [showLegacy, setShowLegacy] = useState(false);

  // Redirect authenticated users in an effect (not during render) to avoid
  // "Cannot update a component while rendering a different component" warning.
  useEffect(() => {
    if (user) navigate(user.profile_complete ? '/discover' : '/profile/setup', { replace: true });
  }, [user, navigate]);

  // Warm the direct-Google config cache so the first tap of the
  // Google button doesn't stall on a network round-trip. Must live
  // ABOVE the early return so hook order stays stable.
  useEffect(() => { getGoogleAuthConfig().catch(() => {}); }, []);

  if (user) return null;

  const handleGoogleAuth = () => {
    // Feb 2026 — direct-Google flow when the backend has GOOGLE_CLIENT_ID
    // + GOOGLE_CLIENT_SECRET configured; otherwise falls back to the
    // Emergent-hosted OAuth handoff so sign-in keeps working while
    // credentials are being rotated. The Client ID is fetched at
    // runtime from GET /api/auth/google/config so we don't need a
    // REACT_APP_* build-time env var (which would require a redeploy
    // on every rotation).
    //
    // REMINDER: DO NOT HARDCODE URLs OR ADD FALLBACK REDIRECTS in the
    // Emergent branch — the trailing/leading-slash sensitivity there
    // has bitten us before.
    launchGoogleSignIn();
  };

  // Sign in with Apple (web) — popup flow via AppleID.auth.signIn().
  // SDK is loaded lazily on first click. Services ID + return URL must be
  // registered in Apple Developer console.
  const APPLE_SERVICES_ID = process.env.REACT_APP_APPLE_SERVICES_ID || '';
  const APPLE_ENABLED = Boolean(APPLE_SERVICES_ID);

  const loadAppleSdk = () =>
    new Promise((resolve, reject) => {
      if (window.AppleID) return resolve(window.AppleID);
      const existing = document.getElementById('apple-signin-sdk');
      if (existing) {
        existing.addEventListener('load', () => resolve(window.AppleID));
        existing.addEventListener('error', reject);
        return;
      }
      const s = document.createElement('script');
      s.id = 'apple-signin-sdk';
      s.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
      s.async = true;
      s.onload = () => resolve(window.AppleID);
      s.onerror = reject;
      document.head.appendChild(s);
    });

  const handleAppleAuth = async () => {
    if (!APPLE_ENABLED) { toast.error('Apple Sign-In not configured'); return; }
    setLoading(true);
    try {
      await loadAppleSdk();
      window.AppleID.auth.init({
        clientId: APPLE_SERVICES_ID,
        scope: 'name email',
        redirectURI: window.location.origin + '/auth/apple/callback',
        state: 'signin',
        usePopup: true,
      });
      const data = await window.AppleID.auth.signIn();
      // data = { authorization: {id_token, code, state}, user?: {name: {firstName, lastName}, email} }
      const idToken = data.authorization?.id_token || '';
      // Peek at the token claims in-browser for debugging (no signature verify).
      // Helps diagnose why the backend might reject: shows aud, iss, exp.
      try {
        const payloadB64 = idToken.split('.')[1] || '';
        const decoded = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
        console.info('[Apple Sign-In] token claims:', {
          aud: decoded.aud, iss: decoded.iss, sub: decoded.sub,
          exp: decoded.exp, iat: decoded.iat,
          email_verified: decoded.email_verified,
          is_private_email: decoded.is_private_email,
        });
      } catch (dbgErr) { /* ignore */ }
      const payload = {
        identity_token: idToken,
        authorization_code: data.authorization?.code,
      };
      if (data.user?.name) {
        payload.full_name = {
          given_name: data.user.name.firstName,
          family_name: data.user.name.lastName,
        };
      }
      const res = await api.post('/auth/apple/session', payload);
      login(res.data, res.data.session_token);
      navigate(res.data.profile_complete ? '/discover' : '/profile/setup');
    } catch (err) {
      // Apple popup close returns { error: "popup_closed_by_user" } — silent.
      if (err?.error === 'popup_closed_by_user') return;
      console.error('[Apple Sign-In] failed:', err?.response?.data || err);
      toast.error(err.response?.data?.detail || 'Apple sign-in failed');
    } finally { setLoading(false); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) { toast.error(t('auth.errLoginInputs')); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { email: form.email, password: form.password });
      login(res.data, res.data.session_token);
      // Force-migration gate: legacy email-only users must link a Google/Phone/Apple
      // account before they can proceed to the app (per June 2026 auth restriction).
      if (res.data.must_link_provider) {
        navigate('/link-account');
      } else {
        navigate(res.data.profile_complete ? '/discover' : '/profile/setup');
      }
    } catch (err) { toast.error(err.response?.data?.detail || t('auth.errLogin')); }
    finally { setLoading(false); }
  };

  const inputCls = "rounded-xl bg-[#F8F9FA] border-black/[0.06] text-[#1A1A1A] placeholder:text-zinc-400 focus-visible:ring-[#EF4444]/50 focus-visible:border-[#EF4444]/50 px-4 py-3.5 text-sm";

  return (
    <div className="min-h-screen relative flex items-center justify-center px-6 py-12" data-testid="auth-page">
      <CouplesSlideshow overlay="dark" showCaption />
      <div className="relative w-full max-w-md rounded-3xl bg-white/95 backdrop-blur-md shadow-2xl border border-white/60 p-6 sm:p-8">
        {/* Auth card wrapper — floats over slideshow, semi-opaque so hero legibility is preserved */}
      <div className="w-full max-w-sm">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-zinc-400 hover:text-[#1A1A1A] mb-10 transition-colors" data-testid="back-to-home">
          <ArrowLeft size={16} /> <span className="text-sm">{t('auth.back')}</span>
        </button>

        <div className="flex items-center gap-4 mb-3">
          <img src="/logo-heart.png" alt="" className="w-16 h-16 object-contain flex-shrink-0" data-testid="auth-brand-heart" />
          <h1 className="text-[28px] font-bold tracking-tight leading-none">{t('auth.brand')}</h1>
        </div>
        <p className="text-zinc-500 text-sm mb-8">{t('auth.tagline')}</p>

        <button onClick={handleGoogleAuth} data-testid="google-auth-btn"
          className="w-full rounded-xl bg-[#F8F9FA] border border-black/[0.06] hover:bg-[#F1F3F5] py-3.5 mb-6 text-sm font-medium transition-all flex items-center justify-center gap-3">
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          {t('auth.continueGoogle')}
        </button>

        {APPLE_ENABLED && (
          <button onClick={handleAppleAuth} data-testid="apple-auth-btn" disabled={loading}
            className="w-full rounded-xl bg-black text-white hover:bg-zinc-800 py-3.5 mb-6 text-sm font-medium transition-all flex items-center justify-center gap-3 disabled:opacity-60">
            <svg className="w-[18px] h-[18px] -mt-0.5" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
              <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
            </svg>
            Continue with Apple
          </button>
        )}

        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 h-px bg-black/[0.06]" />
          <span className="text-xs text-zinc-500">{t('auth.or')}</span>
          <div className="flex-1 h-px bg-black/[0.06]" />
        </div>

        {/* Phone (Twilio SMS) — third of the three allowed providers (June 2026). */}
        <PhoneAuthPanel
          referralCode={form.referral_code}
          onAuthSuccess={(res) => {
            login(res, res.session_token);
            if (res.must_link_provider) {
              navigate('/link-account');
            } else {
              navigate(res.profile_complete ? '/discover' : '/profile/setup');
            }
          }}
        />

        {/* Existing users only — email/password signup is disabled per June 2026 auth
            restriction. Legacy accounts can still sign in here; they're then bounced
            to /link-account to attach a Google/Phone/Apple provider before proceeding. */}
        <div className="mt-6 pt-5 border-t border-black/[0.06]">
          {!showLegacy ? (
            <button
              type="button"
              data-testid="show-legacy-login"
              onClick={() => setShowLegacy(true)}
              className="w-full text-center text-[12px] font-semibold text-zinc-500 hover:text-[#0F0F10] transition-colors"
            >
              {t('auth.legacyExistingUser', 'Existing user? Sign in with email')}
            </button>
          ) : (
            <form onSubmit={handleLogin} className="space-y-3" data-testid="legacy-login-form">
              <p className="text-[11px] text-zinc-500 leading-snug">
                {t('auth.legacyNotice', "For existing accounts only. You'll be asked to link a Google, Phone, or Apple account after signing in.")}
              </p>
              <Input data-testid="login-email" type="email" placeholder={t('auth.emailPh')} className={inputCls}
                value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} />
              <Input data-testid="login-password" type="password" placeholder={t('auth.passwordPh')} className={inputCls}
                value={form.password} onChange={e => setForm(p => ({...p, password: e.target.value}))} />
              <div className="flex items-center justify-between -mt-1">
                <button
                  type="button"
                  data-testid="hide-legacy-login"
                  onClick={() => setShowLegacy(false)}
                  className="text-[11px] font-semibold text-zinc-500 hover:text-[#0F0F10]">
                  {t('auth.legacyCancel', 'Cancel')}
                </button>
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={() => setForgotOpen(true)}
                  className="text-[11px] font-semibold text-zinc-500 hover:text-[#0F0F10] transition-colors">
                  {t('auth.forgotPassword')}
                </button>
              </div>
              <button type="submit" data-testid="login-submit" disabled={loading}
                className="w-full bg-[#0F0F10] text-white py-3 rounded-xl text-sm font-semibold transition-all hover:bg-[#1F1F22] disabled:opacity-50 active:scale-[0.98]">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t('auth.signInBtn')}
              </button>
            </form>
          )}
        </div>
      </div>

      <ForgotPasswordDialog
        open={forgotOpen}
        onOpenChange={setForgotOpen}
        initialEmail={form.email}
      />
      </div>
    </div>
  );
}
